import { EventAggregator } from 'aurelia-event-aggregator';
import {
	autoinject,
	bindable,
	BindingEngine,
	bindingMode,
	computedFrom,
	customElement,
	PLATFORM,
	useView,
} from 'aurelia-framework';
import { I18N } from 'aurelia-i18n';

import {
	ContextMenuEvent,
	ContextMenuItem,
} from '~/components/context-menu-list/context-menu-list';
import { ContextMenuPosition } from '~/components/context-menu/context-menu';
import { FieldModel } from '~/models/form-model';
import { Clipboard } from '~/utils/clipboard';

@autoinject
@customElement('field-holder')
@useView(PLATFORM.moduleName('./field-holder.html'))
export class FieldHolder {
	@bindable({ defaultBindingMode: bindingMode.oneWay })
	private label: string = '';

	/**
	 * This should be set to true for cases where the label should NOT push the input element down. It does this by the
	 * removal the label element the from the DOM of the component.
	 * @type {boolean}
	 */
	@bindable({ defaultBindingMode: bindingMode.oneWay })
	private isLabelHidden: boolean = false;

	/**
	 * This should be set to true for cases where the label should be to the left of the input rather than
	 * the default location of the input border top
	 * @type {boolean}
	 */
	@bindable({ defaultBindingMode: bindingMode.oneWay })
	private isLabelLeft: boolean = false;

	@bindable({ defaultBindingMode: bindingMode.oneWay })
	private name: string = '';

	@bindable({ defaultBindingMode: bindingMode.oneWay })
	private required: boolean = false;

	@bindable({ defaultBindingMode: bindingMode.oneWay })
	private hidden: boolean = false;

	@bindable({ defaultBindingMode: bindingMode.oneWay })
	private isReadonly: boolean = false;

	/**
	 * hasFlexibleHeight is utilized by <a-description> to render a value
	 * with a flexible (not-fixed) height. This is for rendering long
	 * error messages on the transaction-centre page.
	 */
	@bindable({ defaultBindingMode: bindingMode.oneWay })
	private hasFlexibleHeight: boolean = false;

	/**
	 * The value to display if 'readonly' is true.
	 */
	@bindable({ defaultBindingMode: bindingMode.oneWay })
	private readonlyValue: string = '';

	/**
	 * If 'readonly' is true, setting this will cause the value to be wrapped in an anchor with this as the href.
	 */
	@bindable({ defaultBindingMode: bindingMode.oneWay })
	private anchorHref: string = '';

	/**
	 * A complementary description value rendered next to the value.
	 * Generally used to display a "description" for a "code".
	 */
	@bindable({ defaultBindingMode: bindingMode.oneWay })
	private readonlyDescription: string = '';

	/**
	 * Align the text right for readonly display mode.
	 * This is utilized by number.ts
	 */
	@bindable({ defaultBindingMode: bindingMode.oneWay })
	private readonlyAlignRight: boolean = false;

	@bindable({
		defaultBindingMode: bindingMode.oneWay,
		changeHandler: 'validationMessageChanged',
	})
	private readonly validationMessage: string = '';

	/**
	 * If refInputElem is passed from the field to the <field-holder> component, `inputmaskMessage`
	 * is passed to display a tooltip which warns user about invalid key typing
	 */
	@bindable({
		defaultBindingMode: bindingMode.oneWay,
		changeHandler: 'inputmaskMessageChanged',
	})
	private readonly inputmaskMessage?: string;

	/**
	 * An optional setting.
	 * This property was added to determine whether a context menu relating to
	 * Acurity fields should be added or not.
	 */
	@bindable({
		defaultBindingMode: bindingMode.oneWay,
		changeHandler: 'fieldModelChanged',
	})
	private fieldModel?: FieldModel;

	/**
	 * iconBeforeClass allows you to define an icon class like "fas fa-dollar-sign" which will
	 * insert an icon to appear after the field element.
	 */
	@bindable({ defaultBindingMode: bindingMode.oneWay })
	private readonly iconBeforeClass: string = '';

	/**
	 * iconAfterClass allows you to define an icon class like "far fa-calendar-alt" which will
	 * insert an icon to appear after the field element.
	 */
	@bindable({ defaultBindingMode: bindingMode.oneWay })
	private readonly iconAfterClass: string = '';

	/**
	 * reserveIconSpace is utilize by <a-autocomplete> as it managed its own icon on
	 * the right-side of the field. It helps to avoid icon overlap.
	 */
	@bindable({ defaultBindingMode: bindingMode.oneWay })
	private readonly reserveIconSpace: boolean = false;

	/**
	 * disableEmbedAfterPointerEvents will allow you to click-through the
	 * div where you can embed additional icons or buttons.
	 */
	@bindable({ defaultBindingMode: bindingMode.oneWay })
	private readonly disableEmbedAfterPointerEvents: boolean = false;

	/**
	 * refInputEl is the <input> / <select> / <textarea> passed from the field to
	 * the <field-holder> component.
	 */
	@bindable({
		defaultBindingMode: bindingMode.oneWay,
		changeHandler: 'refInputElemChanged',
	})
	private refInputElem?:
		| HTMLInputElement
		| HTMLSelectElement
		| HTMLTextAreaElement;

	private contextMenuPos?: ContextMenuPosition;

	private errorIconElem?: HTMLElement;

	private isOverflowHover = false;

	/**
	 * isContextMenuDisabled allows to hide the context menu passed from the field to
	 * the <field-holder> component.
	 */
	@bindable({
		defaultBindingMode: bindingMode.oneWay,
	})
	private readonly isContextMenuDisabled: boolean = false;

	constructor(
		private readonly element: Element,
		private readonly i18n: I18N,
	) {}

	private attached(): void {
		if (!this.errorIconElem) {
			throw new Error('"errorIconElem" is not set.');
		}
		this.errorIconElem.addEventListener('mouseover', this.onErrorIconHover);

		if (!this.refReadonlyContainer) {
			throw new Error('"refReadonlyContainer" is not set.');
		}
		this.refReadonlyContainer.addEventListener(
			'mouseover',
			this.onRefReadonlyContainerHover,
		);
		this.refReadonlyContainer.addEventListener(
			'mouseout',
			this.onRefReadonlyContainerOut,
		);
	}

	private detached(): void {
		if (this.errorIconElem) {
			this.errorIconElem.removeEventListener(
				'mouseover',
				this.onErrorIconHover,
			);

			// Dispose tooltips
			$(this.errorIconElem).tooltip('dispose');
		}
		if (this.refInputElem) {
			this.refInputElem.removeEventListener(
				'focus',
				this.onFieldInputFocus,
			);
			this.refInputElem.removeEventListener(
				'blur',
				this.onFieldInputBlur,
			);

			// Dispose tooltips
			$(this.refInputElem).tooltip('dispose');
		}
	}

	private fieldModelChanged(
		newFieldModel: FieldModel,
		oldFieldModel: FieldModel,
	): void {
		if (this.element) {
			if (this.fieldModel) {
				document.body.addEventListener(
					'contextmenu',
					this.onContextMenuClick,
				);
			} else {
				document.body.removeEventListener(
					'contextmenu',
					this.onContextMenuClick,
				);
			}
		}
	}

	private refInputElemChanged(
		newInputEl: HTMLElement,
		oldInputEl: HTMLElement,
	): void {
		if (newInputEl) {
			newInputEl.addEventListener('focus', this.onFieldInputFocus);
			newInputEl.addEventListener('blur', this.onFieldInputBlur);
		}
	}

	private inputmaskMessageChanged(
		newErrorMessage: string,
		oldErrorMessage: string,
	): void {
		if (newErrorMessage) {
			this.showInputMaskHintTooltip();
		} else {
			this.hideInputMaskHintTooltip();
		}
	}

	private readonly onContextMenuClick = (e: MouseEvent): void => {
		const target: HTMLElement = e.target as HTMLElement;
		if (!this.element) {
			return;
		}
		if (target === this.element || this.element.contains(target)) {
			e.preventDefault();
			if (this.contextMenuPos) {
				// If already open, close by unsetting
				this.hideContextMenu();
				return;
			}
			this.contextMenuPos = {
				x: e.pageX,
				y: e.pageY,
			};
		}
	};

	private hideContextMenu = (): void => {
		this.contextMenuPos = undefined;
	};

	private readonly onFieldInputFocus = (e: MouseEvent): void => {
		this.showTooltip();
	};

	private readonly onFieldInputBlur = (): void => {
		this.hideTooltip();
	};

	private readonly onErrorIconHover = (e: MouseEvent): void => {
		this.showTooltip();
	};

	private readonly onRefReadonlyContainerHover = (e: MouseEvent): void => {
		if (!this.isOverflowHover && this.isOverflow) {
			this.isOverflowHover = true;
		}
	};

	private readonly onRefReadonlyContainerOut = (e: MouseEvent): void => {
		this.isOverflowHover = false;
	};

	private validationMessageChanged(newValue: string, oldValue: string): void {
		if (!oldValue && !newValue) {
			// Ignore if both are falsey.
			return;
		}
		// NOTE(Jae): 2020-02-07 - WEBUI-2358
		// Need to wait 1 frame for data changes to propogate and then
		// we update the tooltip. Removing "requestAnimationFrame" will
		// break the Cypress test.
		window.requestAnimationFrame(() => {
			this.showTooltip();
		});
	}

	private constructDefaultTooltip = (
		content: string,
		trigger: Bootstrap.Trigger = 'hover',
	): Bootstrap.TooltipOption => {
		return {
			delay: {
				// NOTE: Jake: 2019-02-08 - WEBUI-1136
				// These values were chosen to align with the previous
				// animation speeds defined in CSS.
				show: 200,
				hide: 100,
			},
			title: content,
			trigger,
			placement: 'right',
			// NOTE: Jake: 2018-02-08 - WEBUI-1192
			// 'container' was added to try and get tooltips to be in the same <iframe> context
			// as the element. Unfortunately tooltip.js wraps the container call in $(document).find()
			// so this doesn't fix the problem.
			// The reason I want this is so tooltips could be tested in Cypress.
			// container: this.errorIconElem.ownerDocument.body,
			template: `
				<div class="tooltip field-holder__error-message" role="tooltip">
					<div class="arrow field-holder__error-message__arrow"></div>
					<div class="tooltip-inner field-holder__error-message__inner"></div>
				</div>
			`.trim(),
			// NOTE: Jake: 2018-11-28 - WEBUI-1192
			// 'selector' must be "true", otherwise the "title" attribute
			// will be overriden. We don't want this so that when you hover over an
			// input, you'll still see "cOZ_Fieldname".
			selector: true as any as string,
		};
	};

	/**
	 * Lazily initialize the validation alert tooltip and display when hovering over the error icon
	 */
	private showTooltip = (): void => {
		if (
			!this.validationMessage ||
			!this.errorIconElem ||
			this.inputmaskMessage // don't show validation tooltip if input mask hint tooltip is displaying
		) {
			$(this.errorIconElem).tooltip('dispose');
			return;
		}

		// Add tooltips
		const defaultTooltip: Bootstrap.TooltipOption =
			this.constructDefaultTooltip(this.validationMessage);

		$(this.errorIconElem).tooltip('dispose');
		$(this.errorIconElem).tooltip(defaultTooltip);

		$(this.errorIconElem).tooltip('update');
		$(this.errorIconElem).tooltip('show');
	};

	private hideTooltip = (): void => {
		if (this.errorIconElem) {
			$(this.errorIconElem).tooltip('hide');
		}
	};

	/**
	 * Lazily initialize the inputmask hint tooltip and display when this method is called.
	 */
	private showInputMaskHintTooltip = (): void => {
		if (!this.inputmaskMessage || !this.refInputElem) {
			return;
		}

		this.hideTooltip();

		const defaultTooltip: Bootstrap.TooltipOption =
			this.constructDefaultTooltip(this.inputmaskMessage, 'manual');

		$(this.refInputElem).tooltip('dispose');
		$(this.refInputElem).tooltip(defaultTooltip);

		$(this.refInputElem).tooltip('update');
		$(this.refInputElem).tooltip('show');
	};

	private hideInputMaskHintTooltip = (): void => {
		if (this.refInputElem) {
			$(this.refInputElem).tooltip('hide');
		}
	};

	/**
	 * onCloseContextMenu is callback fired from <context-menu>
	 */
	private onCloseContextMenu = (
		target: HTMLElement | undefined,
		event: ContextMenuEvent,
	): void => {
		if (!this.element) {
			return;
		}
		if (
			event === 'contextmenu' &&
			(this.element === target || this.element.contains(target))
		) {
			// Ignore action if we clicked the right-click the field
			return;
		}
		this.hideContextMenu();
	};

	@computedFrom('fieldModel', 'fieldModel.name')
	get contextMenuItems(): ContextMenuItem[] {
		if (!this.fieldModel || this.isContextMenuDisabled) {
			return [];
		}
		return [
			{
				label: this.i18n.tr('field-holder.copy-architect-field-name'),
				onClick: this.onClickCopyArchitectFieldNameToClipboard,
				iconClass: 'fa-clone',
			},
			{
				label: this.i18n.tr('field-holder.copy-sql-field-name'),
				onClick: this.onClickCopySQLFieldNameToClipboard,
				iconClass: 'fa-clone',
			},
			{
				label: this.i18n.tr('field-holder.copy-contents'),
				onClick: this.onClickCopyContentsToClipboard,
				iconClass: 'fa-clone',
			},
		];
	}

	@computedFrom('validationMessage', 'inputmaskMessage')
	get errorMessage(): string {
		if (this.inputmaskMessage) {
			return this.inputmaskMessage;
		}
		return this.validationMessage;
	}

	private readonly onClickCopyArchitectFieldNameToClipboard = (
		e: MouseEvent,
	): void => {
		// NOTE: Jake: 2019-02-20 - WEBUI-308
		// The Architect Field Name is the SQL Field Name with 'h' prepended.
		const content = this.name ? 'h' + this.name.trim() : '';
		Clipboard.copy(content);
	};

	private readonly onClickCopySQLFieldNameToClipboard = (
		e: MouseEvent,
	): void => {
		const content = this.name ? this.name.trim() : '';
		Clipboard.copy(content);
	};

	private readonly onClickCopyContentsToClipboard = (e: MouseEvent): void => {
		// NOTE: Jake: 2019-02-21 - WEBUI-308
		// Umit said that the value should be trimmed for "Copy contents to clipboard"
		// NOTE(Jae): 2020-02-10 - WEBUI-2986
		// Changing to *not* trim as a user will want a field like z_Client to retain
		// its whitespace. Most value trimming occurs in the backend already anyway
		// so it should be unnecessary to trim here.
		const content = this.readonlyValue ? this.readonlyValue : '';
		Clipboard.copy(content);
	};

	private refReadonlyContainer?: HTMLElement;

	@computedFrom('fieldModel.value')
	get isOverflow(): boolean {
		if (!this.fieldModel?.value || !this.refReadonlyContainer) {
			return false;
		}

		const area = this.refReadonlyContainer;

		if (area.clientWidth >= area.scrollWidth) {
			return false;
		}

		return true;
	}
}
